import React from 'react';

import Loading from '@pray/shared/components/Loading';
import Image from '@pray/shared/components/ui/Image/Image';
import { isPlaceholderImage } from '@pray/shared/utils/isPlaceholderImage';
import { cn } from '@pray/shared/utils/styles';

import styles from './FundImage.module.scss';

// The slot is a fixed landscape band. Declare its ratio on the element as well as in
// CSS so resolving to a different source never reflows the page — /donate is already
// at half its CLS budget.
const IMAGE_WIDTH = 500;
const IMAGE_HEIGHT = 405;

type FundImageProps = {
  url?: string | null;
  artistImageUrl?: string | null;
  isLoading: boolean;
  className?: string;
};

/**
 * Fund image, then leader profile image, then nothing — the container's brand
 * gradient shows through. The placeholder is rejected here rather than via onError
 * so it never costs a request, and a source that is merely broken still falls back
 * through Image's own chain.
 */
export function resolveFundImageUrl(url?: string | null, artistImageUrl?: string | null): string {
  if (url && !isPlaceholderImage(url)) return url;
  if (artistImageUrl && !isPlaceholderImage(artistImageUrl)) return artistImageUrl;
  return '';
}

export default function FundImage({ url, artistImageUrl, isLoading, className }: FundImageProps) {
  if (isLoading) {
    return <Loading className="!lg:m-5 !lg:mx-auto !mb-6 h-64 w-full" />;
  }

  const src = resolveFundImageUrl(url, artistImageUrl);

  // Whichever image did not win is still worth trying if the winner 404s at runtime.
  const fallbackImage =
    artistImageUrl && artistImageUrl !== src && !isPlaceholderImage(artistImageUrl) ? artistImageUrl : '';

  return (
    <div className={cn(styles.imageContainer, className)}>
      {/* `fallback` is a format swap by default (.jpg -> .png); point it at the source
          itself so the <img> never speculatively requests a sibling that isn't there. */}
      <Image
        src={src}
        fallback={src}
        fallbackImage={fallbackImage}
        loading="eager"
        width={IMAGE_WIDTH}
        height={IMAGE_HEIGHT}
      />
    </div>
  );
}
